import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ProductVariant } from '../productSlice';
import type { ProductKind } from '../../utils/productKind';
import type { ServiceSalesFigures } from '../../utils/serviceSalesFigures';
import { getProductRowDisplayName } from '../../utils/productDisplayName';

export type WeeklyProduct = {
  bp_product_name: string;
  bp_product_sub_category?: string;
  MassSold?: number;
  Quantity: number;
  Sales: number;
  bp_product_buying_price?: number;
  bp_product_selling_price?: number;
  bp_product_unit_of_mass?: string;
  bp_product_price_type?: 'item' | 'unit' | string;
};

export interface WeeklyProductsMeta extends ServiceSalesFigures {
  totalSales: number;
  totalItems: number;
  dailyAverageSales: number;
  totalMassSold: number;
}

export interface WeeklyMostSellingState {
  WeeklyMostSelling: WeeklyProduct[];
  totalDocs: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
  metadata: WeeklyProductsMeta;
}

export interface BackofficeProductsSliceObject {
  WeeklyMostSelling: WeeklyMostSellingState;
  list: ProductsListState;
  productSalesByDay: Record<
    string,
    Array<{ date: string; sales: number; quantity: number }>
  >;
  updatingProduct: boolean;
}

// Backoffice products list types
export type BackofficeProduct = {
  _id: string;
  bp_product_name: string;
  bp_product_category?: string;
  bp_product_sub_category?: string;
  bp_product_unit_of_mass?: string;
  bp_product_buying_price?: number | null;
  bp_product_selling_price?: number | null;
  bp_product_quantity: number | null;
  bp_product_mass?: string | number;
  bp_product_size?: string;
  bp_product_model?: string;
  bp_minimum_stock_level?: number;
  bp_product_price_type?: 'item' | 'unit' | string;
  /** Missing on items saved before services existed: a product. */
  bp_product_kind?: ProductKind;
  bp_product_image?: string;
  bp_product_barcode?: string;
  bp_product_is_favourite?: boolean;
  bp_product_has_variants?: boolean;
  bp_product_variant_attributes?: string[];
  bp_product_variants?: ProductVariant[];
};

export interface ProductsListState {
  items: BackofficeProduct[];
  loading: boolean;
  error?: string | null;
  total: number;
  page: number;
  pageSize: number;
  selectedProductId?: string | null;
  searchTerm: string;
  // Table-local search state (separate from any global search)
  tableSearchTerm: string;
  tableSearchItems: BackofficeProduct[];
  tableSearchTotal: number;
  tableSearchPage: number;
  tableSearchPageSize: number;
}

const initialProductsList: ProductsListState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  pageSize: 500,
  selectedProductId: null,
  searchTerm: '',
  tableSearchTerm: '',
  tableSearchItems: [],
  tableSearchTotal: 0,
  tableSearchPage: 1,
  tableSearchPageSize: 100,
};

const initialState: BackofficeProductsSliceObject = {
  WeeklyMostSelling: {
    WeeklyMostSelling: [],
    totalDocs: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
    metadata: {
      totalSales: 0,
      totalItems: 0,
      dailyAverageSales: 0,
      totalMassSold: 0,
    },
  },
  list: initialProductsList,
  productSalesByDay: {},
  updatingProduct: false,
};

const productsSlice = createSlice({
  name: 'backofficeProducts',
  initialState,
  reducers: {
    setWeeklyMostSelling: (state, action: PayloadAction<any>) => {
      const payload = action.payload;
      const array = payload?.data || [];
      const mapped = array.map((item: any) => {
        const product = item?.productInformation || {};
        const variants = Array.isArray(product?.bp_product_variants)
          ? product.bp_product_variants
          : [];
        const isVariant = Boolean(product?.bp_product_has_variants) && variants.length > 0;
        const firstVariant = isVariant ? variants[0] : null;

        const sellingPrice = (() => {
          const snapshot =
            item?.bp_product_selling_price_snapshot ?? item?.snapshotSellingPrice;
          if (snapshot != null) return Number(snapshot || 0);
          const variantInfo =
            item?.variantInformation || item?.bp_variant_information;
          if (variantInfo?.bp_variant_selling_price != null)
            return Number(variantInfo.bp_variant_selling_price);
          if (product?.bp_product_selling_price != null)
            return Number(product.bp_product_selling_price);
          if (isVariant) {
            const qty = Number(item?.totalQuantitySold || 0);
            const sales = Number(item?.totalSales || 0);
            if (qty > 0) return sales / qty;
            return Number(firstVariant?.bp_variant_selling_price || 0);
          }
          return 0;
        })();

        const buyingPrice = (() => {
          const snapshot =
            item?.bp_product_buying_price_snapshot ?? item?.snapshotBuyingPrice;
          if (snapshot != null) return Number(snapshot || 0);
          const variantInfo =
            item?.variantInformation || item?.bp_variant_information;
          if (variantInfo?.bp_variant_buying_price != null)
            return Number(variantInfo.bp_variant_buying_price);
          if (product?.bp_product_buying_price != null)
            return Number(product.bp_product_buying_price);
          if (isVariant) {
            return Number(firstVariant?.bp_variant_buying_price || 0);
          }
          return 0;
        })();

        const productName = getProductRowDisplayName(item);

        return {
          bp_product_name: productName,
          bp_product_sub_category: product?.bp_product_sub_category,
          MassSold: item?.totalMassSold,
          Quantity: item?.totalQuantitySold,
          Sales: item?.totalSales,
          bp_product_buying_price: buyingPrice,
          bp_product_selling_price: sellingPrice,
          bp_product_unit_of_mass: product?.bp_product_unit_of_mass ?? '',
          bp_product_price_type: product?.bp_product_price_type ?? '',
        };
      });

      state.WeeklyMostSelling = {
        WeeklyMostSelling: mapped,
        totalDocs: payload?.total ?? mapped.length,
        totalPages: payload?.totalPages ?? 1,
        currentPage: payload?.currentPage ?? 1,
        perPage: payload?.perPage ?? mapped.length,
        metadata: {
          totalSales: payload?.totalSalesAmount ?? 0,
          totalItems: payload?.totalItemsSold ?? 0,
          dailyAverageSales: payload?.dailyAverage ?? 0,
          totalMassSold: payload?.totalMassSold ?? 0,
          totalServicesSold: payload?.totalServicesSold ?? 0,
          totalServiceHoursSold: payload?.totalServiceHoursSold ?? 0,
          hasServiceSales: payload?.hasServiceSales === true,
          hasServiceHourSales: payload?.hasServiceHourSales === true,
        },
      };
    },
    ensureProductsListInitialized: state => {
      // Defensive migration for older persisted state without `list`
      // or when state was partially rehydrated
      if (!state.list || !Array.isArray(state.list.items)) {
        state.list = { ...initialProductsList };
      }
      // Ensure new keys exist on hydrated state
      if (typeof state.list.searchTerm !== 'string') state.list.searchTerm = '';
      if (typeof state.list.tableSearchTerm !== 'string')
        state.list.tableSearchTerm = '';
      if (!Array.isArray(state.list.tableSearchItems))
        state.list.tableSearchItems = [];
      if (typeof state.list.tableSearchTotal !== 'number')
        state.list.tableSearchTotal = 0;
      if (typeof state.list.tableSearchPage !== 'number')
        state.list.tableSearchPage = 1;
      if (typeof state.list.tableSearchPageSize !== 'number')
        state.list.tableSearchPageSize = 100;
      if (typeof (state.list as any).pageSize !== 'number')
        state.list.pageSize = 100;
    },
    // Products list reducers
    setProducts: (
      state,
      action: PayloadAction<{
        items: BackofficeProduct[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      state.list.items = action.payload.items;
      if (typeof action.payload.total === 'number')
        state.list.total = action.payload.total;
      if (typeof action.payload.page === 'number')
        state.list.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.list.pageSize = action.payload.pageSize;
    },
    addProduct: (state, action: PayloadAction<BackofficeProduct>) => {
      state.list.items = [action.payload, ...state.list.items];
      state.list.total = state.list.total + 1;
    },
    updateProduct: (state, action: PayloadAction<BackofficeProduct>) => {
      const idx = state.list.items.findIndex(p => p._id === action.payload._id);
      if (idx !== -1)
        state.list.items[idx] = { ...state.list.items[idx], ...action.payload };
    },
    removeProduct: (state, action: PayloadAction<string>) => {
      const deletedId = action.payload;
      const beforeItemsLength = state.list.items.length;
      state.list.items = state.list.items.filter(p => p._id !== deletedId);
      if (state.list.items.length < beforeItemsLength) {
        state.list.total = Math.max(0, state.list.total - 1);
      }

      // Also remove from table search results if present
      const beforeSearchLen = state.list.tableSearchItems.length;
      state.list.tableSearchItems = state.list.tableSearchItems.filter(
        p => p._id !== deletedId,
      );
      if (state.list.tableSearchItems.length < beforeSearchLen) {
        state.list.tableSearchTotal = Math.max(
          0,
          state.list.tableSearchTotal - 1,
        );
      }

      // Clear selection if it was the deleted product
      if (state.list.selectedProductId === deletedId) {
        state.list.selectedProductId = null;
      }
    },
    setProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.list.loading = action.payload;
    },
    setProductsError: (state, action: PayloadAction<string | null>) => {
      state.list.error = action.payload;
    },
    setProductsPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (typeof action.payload.page === 'number')
        state.list.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.list.pageSize = action.payload.pageSize;
    },
    setSelectedProductId: (state, action: PayloadAction<string | null>) => {
      state.list.selectedProductId = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.list.searchTerm = action.payload;
    },
    // Table search reducers
    setTableSearchTerm: (state, action: PayloadAction<string>) => {
      state.list.tableSearchTerm = action.payload;
      // Reset table-search paging when term changes
      state.list.tableSearchPage = 1;
    },
    setTableSearchResults: (
      state,
      action: PayloadAction<{
        items: BackofficeProduct[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      state.list.tableSearchItems = action.payload.items || [];
      if (typeof action.payload.total === 'number')
        state.list.tableSearchTotal = action.payload.total;
      if (typeof action.payload.page === 'number')
        state.list.tableSearchPage = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.list.tableSearchPageSize = action.payload.pageSize;
    },
    setTableSearchPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (typeof action.payload.page === 'number')
        state.list.tableSearchPage = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.list.tableSearchPageSize = action.payload.pageSize;
    },
    clearTableSearch: state => {
      state.list.tableSearchTerm = '';
      state.list.tableSearchItems = [];
      state.list.tableSearchTotal = 0;
      state.list.tableSearchPage = 1;
      state.list.tableSearchPageSize = 100;
    },
    setProductSalesSeries: (
      state,
      action: PayloadAction<{
        productId: string;
        series: Array<{ date: string; sales: number; quantity: number }>;
      }>,
    ) => {
      state.productSalesByDay[action.payload.productId] = action.payload.series;
    },
    setUpdatingProduct: (state, action: PayloadAction<boolean>) => {
      state.updatingProduct = action.payload;
    },
  },
});

export const {
  setWeeklyMostSelling,
  ensureProductsListInitialized,
  setProducts,
  addProduct,
  updateProduct,
  removeProduct,
  setProductsLoading,
  setProductsError,
  setProductsPagination,
  setSelectedProductId,
  setSearchTerm,
  setTableSearchTerm,
  setTableSearchResults,
  setTableSearchPagination,
  clearTableSearch,
  setProductSalesSeries,
  setUpdatingProduct,
} = productsSlice.actions;
export default productsSlice.reducer;
